import Link from "@/components/LocalizedLink";
import { ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import type { IndustryContextKind } from "@/content/site";

type IndustryCardProps = {
  code: string;
  title: string;
  description: string;
  href: string;
  ctaLabel: string;
  typeLabel?: string;
  kind?: IndustryContextKind;
  headingLevel?: 3 | 4;
};

const IndustryCard = ({
  code,
  title,
  description,
  href,
  ctaLabel,
  typeLabel,
  kind = "detailed-guide",
  headingLevel = 3,
}: IndustryCardProps) => {
  const Heading = headingLevel === 4 ? "h4" : "h3";

  return (
    <Link
      to={href}
      className="group block h-full min-w-0"
      data-event={kind === "detailed-guide" ? "industry_page_cta_click" : "secondary_cta_click"}
      data-event-surface={kind === "detailed-guide" ? "industry_guide_card" : "industry_solution_pattern_card"}
      data-event-target={href}
      data-event-industry-slug={kind === "detailed-guide" ? href.split("/").filter(Boolean).slice(-1)[0] : undefined}
    >
      <div
        className={cn(
          "system-card relative h-full overflow-hidden p-6 transition-colors duration-400 group-hover:border-primary/35 group-hover:bg-primary/[0.025] md:p-7",
          kind === "solution-pattern" && "bg-primary/[0.018]",
        )}
      >
        <div className="absolute left-0 top-0 h-full w-px bg-primary/0 transition-colors duration-400 group-hover:bg-primary/55" aria-hidden="true" />
        <div className="mb-5 flex items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-3">
            <span className="shrink-0 font-mono text-[10px] uppercase tracking-[0.16em] text-primary">{code}</span>
            {typeLabel ? (
              <span className="border-l border-border/60 pl-3 font-mono text-[9px] uppercase tracking-[0.12em] text-content-secondary">
                {typeLabel}
              </span>
            ) : null}
          </div>
          <span className="flex h-8 w-8 items-center justify-center border border-border/70 bg-card shadow-[0_1px_0_hsl(var(--foreground)/0.04)] transition-colors duration-400 group-hover:border-primary/45">
            <ArrowRight className="h-3.5 w-3.5 text-muted-foreground/70 transition-all duration-400 group-hover:translate-x-0.5 group-hover:text-primary" aria-hidden="true" />
          </span>
        </div>
        <Heading className="mb-3 text-[15px] font-semibold leading-snug text-foreground">{title}</Heading>
        <p className="mb-8 text-[13.5px] leading-[1.72] text-muted-foreground">{description}</p>
        <span className={cn("font-mono text-[10px] tracking-[0.12em] uppercase text-content-secondary transition-colors duration-400 group-hover:text-primary")}>
          {ctaLabel}
        </span>
      </div>
    </Link>
  );
};

export default IndustryCard;
